import type { AuditEntry } from '../api/types';
import { formatInstantWithZone } from './time';

/** Design-system seed: the audit trail as a change log ("who changed this and when", R-ACC-03). */
export function ChangeLog({ entries }: { entries: AuditEntry[] }) {
  if (entries.length === 0) return <p className="muted">No changes recorded.</p>;
  return (
    <ol className="changelog">
      {entries.map((e) => (
        <li key={e.id} className="changelog-entry">
          <div className="changelog-head">
            <code className="action">{e.action}</code>
            <span className="muted">
              by {e.actor.label} ({e.actor.type}) · {formatInstantWithZone(e.occurredAt)}
            </span>
          </div>
          <div className="changelog-meta muted">
            {e.resourceType} {e.resourceId.slice(0, 8)}… · request {e.requestId.slice(0, 12)}
          </div>
          {e.before != null || e.after != null ? (
            <details>
              <summary>diff</summary>
              <Diff before={e.before} after={e.after} />
            </details>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function Diff({ before, after }: { before: unknown; after: unknown }) {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)])).sort();
  const changed = keys.filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
  if (changed.length === 0) return <p className="muted">No field changed.</p>;
  return (
    <table className="diff">
      <tbody>
        {changed.map((k) => (
          <tr key={k}>
            <th scope="row">{k}</th>
            <td className="diff-before">{k in b ? JSON.stringify(b[k]) : '—'}</td>
            <td className="diff-after">{k in a ? JSON.stringify(a[k]) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
